import React, { useEffect, useReducer, useState } from "react";
import { DocumentToolbarConstants, MessageToolbarConstants } from "src/Core/Common/Constants";
import { ResourceIdLocators } from "src/helper/ResourceIdLocators";
import { useDispatch, useSelector } from "react-redux";
import Checkbox, { CheckBoxSize } from "../Common/ui/Checkbox";
import { IDocumentRequestListModel } from "src/Core/ViewModels/documentRequest/DocumentRequestModel";
import { DetailedStatus, FolderType } from "src/Core/Common/Enums";
import { BellIcon, MoveToIcon } from "src/assets/images/svg/IconCollection";
import { enableEditPermission, isDelegatedUser, isFolderOwner } from "src/helper/HelperFunctions";
import { DeleteIcon, ResendAccessLinkIcon } from "src/assets/images/svg/IconCollection";
import { ApplicationState } from "src/store";
import { fetchUserOwnedFolders } from "src/actionCreators/folderActionCreators";
import { IFolderModel } from "src/Core/ViewModels/Folder/FolderModel";
import { receivedRequestReducer } from "src/reducers/selectedRequestReducer";
import { MovingRequestActions } from "src/reducers/movingRequestReducer";
import { requestMoveToFolder } from "src/actionCreators/documentRequestActionCreators";
import { CustomModal } from "../Common/Modal/CustomModal";
import { IUserProfile } from "src/Core/ViewModels/Profile/ProfileViewModel";


interface IDocumentToolbarProps {
    isAllSelected: boolean;
    isAnySelected: boolean;
    isArchive?: boolean;
    folder?: IFolderModel;
    showSelectAllCheckbox: boolean;
    selectedDocumentList: IDocumentRequestListModel[];
    handleOnSelectAll: (isSelect: boolean) => void;
    resetSelection: () => void;
    handleDeleteDocumentRequest: () => void;
    handleDocumentResendAccessLink: () => void;
    handleDocumentSendReminder: () => void
}

export const DocumentToolbar: React.FC<IDocumentToolbarProps> = ({
    isAllSelected,
    isAnySelected,
    isArchive,
    folder,
    selectedDocumentList,
    handleOnSelectAll,
    showSelectAllCheckbox,
    resetSelection,
    handleDeleteDocumentRequest,
    handleDocumentResendAccessLink,
    handleDocumentSendReminder
}) => {
    const dispatch = useDispatch();
    const [showDelete, toggleShowDelete] = useState(false);
    const isSendReminderEnabled = isAnySelected ? selectedDocumentList.length === 1 ? selectedDocumentList[0].status !== DetailedStatus.COMPLETED : true : false;
    const checkBoxChangeHandler = (e: any) => {
        handleOnSelectAll(e.target.checked);
    };
    const [selectedRequest, dispatchSelectedRequestActions] = useReducer(receivedRequestReducer, {
        requests: [],
        isAllSelected: false,
    });

    useEffect(() => {
        dispatch(fetchUserOwnedFolders());
    }, []);

    const user = useSelector((state: ApplicationState) => state.userProfile);
    const userOwnedFolders = useSelector((appState: ApplicationState) => appState.folderState.userOwnedFolders);
    const mailboxData = useSelector((state: ApplicationState) => state.commonState.mailboxUserData);
    const isMoveToFolderEnabled =
        userOwnedFolders &&
        userOwnedFolders.some(folder => {
            return isFolderOwner(folder, user, mailboxData);
        });

    const isMoveEnabled = (folder?: IFolderModel) => {

        if (!folder || isFolderOwner(folder, user,mailboxData)) {
                    return true;
        }
            return false;
    };
    const isMoveToEnabled = isAnySelected
        ? (selectedDocumentList.some(document => document.status === DetailedStatus.COMPLETED) && isMoveEnabled(folder)) && isMoveToFolderEnabled
        : false;

    const deleteDocumentRequestSubmit = () => {
        handleDeleteDocumentRequest();
        toggleShowDelete(false);
    };

    const handleOnClickMoveToFolder = () => {
        const folderType = isArchive ? FolderType.ARCHIVE : FolderType.DOCUMENTREQUEST;
        // store selected message as moving messages for moveToFolder dialog
        dispatch({ type: MovingRequestActions.ADD, payload: selectedDocumentList });
        //URL format: .../move-to/folderType/isMovingFolder/isDocumentRequest
        const url = `${window.location.origin}/move-to/${folderType}/false/true`;
        Office.context.ui.displayDialogAsync(
            url,
            { height: 60, width: 50, displayInIframe: true },
            moveToFolderDialogCallback
        );
    };

    const moveToFolderDialogCallback = (asyncResult: Office.AsyncResult<Office.Dialog>) => {
        const dialog = asyncResult.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (args: { message: string; origin: string }) => {
            dialog.close();
            const message = args.message;

            if (message === "cancel") {
                resetSelected();
            } else {
                const destinationFolder: IFolderModel = JSON.parse(message);

                handleMoveToFolder(destinationFolder.folderId, result => {
                    if (result) {
                        resetSelection();
                    }
                });
            }
        });
    };


    const resetSelected = () => {
        dispatchSelectedRequestActions({
            type: "removeAll",
            payload: null,
        });
    };

    const handleMoveToFolder = (folderId: number, callback?: any) => {
        const documentRequestIds = selectedDocumentList.map(document => document.documentRequestId);
        dispatch(
            requestMoveToFolder(documentRequestIds, folderId, callback, isArchive ? ResourceIdLocators.MyFiles.MyFilesBtnRequestMoveTo : ResourceIdLocators.DRL.DRLBtnRequestMoveTo )
        );
        dispatchSelectedRequestActions({
            type: "removeAll",
            payload: null,
        });
    };

    return (
        <div className="message-toolbar">
            <div
                style={{
                    visibility: showSelectAllCheckbox ? "inherit" : "hidden",
                    paddingTop: "2px"
                }}
                className="message-toolbar__action"
            >
                <Checkbox
                    id="mpiCheckbox"
                    text=""
                    size={CheckBoxSize.sm}
                    className="message-toolbar__checkbox"
                    disabled={!enableEditPermission(user,mailboxData,folder)}
                    checked={isAllSelected || isAnySelected}
                    onChange={checkBoxChangeHandler}
                    indeterminate={isAllSelected ? false : isAnySelected}
                    onClick={e => {
                        if (!enableEditPermission(user,mailboxData,folder)) {
                            e.stopPropagation();
                            e.preventDefault();
                        }
                    }}
                />
            </div>
            {(!isDelegatedUser()) && <div className="message-toolbar__action">
                <span
                    className={!isAnySelected ? "toolbar-icon-disabled" : ""}
                    data-resource-id={isArchive ? ResourceIdLocators.MyFiles.MyFilesBtnRequestDelete : ResourceIdLocators.DRL.DRLListBtnDeleteRequest}
                    title={DocumentToolbarConstants.TITLE_DELETE}
                    onClick={() => isAnySelected && toggleShowDelete(true)}
                >
                    <DeleteIcon width={16} height={16} />
                </span>
            </div>}

            <div className="message-toolbar__action" style={{ paddingBottom: "3px" }}>
                <span
                    className={!isAnySelected ? "toolbar-icon-disabled" : ""}
                    data-resource-id={isArchive ? ResourceIdLocators.MyFiles.MyFilesBtnRequestResendAccess : ResourceIdLocators.DRL.DRLListBtnResendAccess}
                    title={DocumentToolbarConstants.TITLE_RESEND_ACCESS_LINK}
                    onClick={() => isAnySelected && handleDocumentResendAccessLink()}
                >
                    <ResendAccessLinkIcon />
                </span>
            </div>

            {!isArchive &&
                <div className="message-toolbar__action">
                    <span
                        className={!isSendReminderEnabled ? "toolbar-icon-disabled" : ""}
                        title={DocumentToolbarConstants.TITLE_SEND_REMINDER}
                        data-resource-id={ResourceIdLocators.DRL.DRLListBtnSendReminder}
                        onClick={() => isSendReminderEnabled && handleDocumentSendReminder()}
                    >
                        <BellIcon />
                    </span>
                </div>
            }
            <div className="message-toolbar__action">
                <span
                    onClick={() => isMoveToEnabled && handleOnClickMoveToFolder()}
                    title={MessageToolbarConstants.TITLE_MOVE_TO_FOLDER}
                    data-resource-id={ isArchive ? ResourceIdLocators.MyFiles.MyFilesBtnRequestMoveTo : ResourceIdLocators.DRL.DRLBtnRequestMoveTo}
                    className={!isMoveToEnabled ? "toolbar-icon-disabled" : ""}
                >
                    <MoveToIcon
                        width={17}
                        height={17}
                    />
                </span>
            </div>

            <CustomModal
                className="delete-message-modal"
                show={showDelete}
                title={DocumentToolbarConstants.TITLE_DELETE}
                cancelButtonName={MessageToolbarConstants.TITLE_CANCEL}
                confirmButtonName={MessageToolbarConstants.TITLE_DELETE}
                onSubmit={deleteDocumentRequestSubmit}
                onHide={() => toggleShowDelete(false)}
                isConfirmModal={true}
            >
                <span>{DocumentToolbarConstants.LISTING_DELETE_REQUEST}</span>
            </CustomModal>
        </div>
    );
};
